// SuperInsightsContent.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import config from "../../../config";
import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";

// Função para substituir **texto** por <strong>texto</strong>
function parseBold(text) {
  if (!text) return "";
  return text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
}

const SuperInsightsContent = ({ textoIA }) => {
  const [apiResult, setApiResult] = useState("");
  const [typedText, setTypedText] = useState("");
  const [typedHtml, setTypedHtml] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDots, setLoadingDots] = useState("");
  const csrfToken = useGetCsrfToken();

  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [typedHtml]);

  // Faz a chamada à API
  const fetchApiResult = async () => {    
    
    const hardcodePrompt = "{'vendas_b2': 482345.78,'vendas_pv': 129874.55} - Esses são os dados de vendas da Pincéis Atlas, gere insights valiosos com base nesses dados";
    
    const promptIA = (textoIA && textoIA.trim() !== "") ? textoIA : hardcodePrompt;

    try {
      setIsLoading(true);       

      let contexto = ''
      if (window.parent || window.top) {
        const urlParams = new URLSearchParams(window.location.search)
        ;['_csrf'].forEach(key => urlParams.delete(key))
        contexto = urlParams.toString()
      }

      const response = await BizinBackend.executeSuperInsights({
        promptIA: promptIA,
        token: csrfToken,
        contexto
      });

      // Tenta diferentes formatos de resposta da API
      let respostaTexto = "";
      
      // Primeiro verifica se é uma string diretamente (mesmo que vazia)
      if (typeof response === "string") {
        respostaTexto = response;
      } 
      // Se response for false ou null, pode ser que a API retornou vazio
      else if (response === false || response === null || response === undefined) {
        respostaTexto = "Nenhuma resposta recebida da API.";
      }
      // Verifica propriedades do objeto - PRIORIDADE PARA 'output' (formato da API externa)
      else if (response && typeof response === "object") {
        // Primeiro tenta 'output' que é o formato esperado da API externa
        if (response.output) {
          respostaTexto = response.output;
        } else if (response.data?.output) {
          respostaTexto = response.data.output;
        } else if (response.resposta) {
          respostaTexto = response.resposta;
        } else if (response.text) {
          respostaTexto = response.text;
        } else if (response.message) {
          respostaTexto = response.message;
        } else if (response.data) {
          if (typeof response.data === "string") {
            respostaTexto = response.data;
          } else if (response.data.resposta) {
            respostaTexto = response.data.resposta;
          } else if (response.data.text) {
            respostaTexto = response.data.text;
          } else {
            // Se for um objeto, tenta converter para string
            respostaTexto = JSON.stringify(response.data, null, 2);
          }
        } else {
          // Se for um objeto sem propriedades conhecidas, converte para string
          respostaTexto = JSON.stringify(response, null, 2);
        }
      } else {
        // Último recurso: converte para string
        respostaTexto = String(response || "Resposta recebida, mas formato não reconhecido.");
      }
      
      // Resposta da API - garante que sempre tenha algo
      if (!respostaTexto || respostaTexto.trim() === "") {
        respostaTexto = "Resposta recebida, mas está vazia ou em formato não reconhecido.";
      }
      
      setApiResult(respostaTexto);

    } catch (error) {
      setApiResult("Erro ao carregar análise. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  // Sempre que 'textoIA' mudar, disparamos a consulta
  useEffect(() => {
    if (textoIA) {
      setApiResult("");
      setTypedText("");
      setTypedHtml("");
      fetchApiResult();
    }
  }, [textoIA]);

  // Animação dos "..." enquanto carrega
  useEffect(() => {
    let dotInterval;
    if (isLoading) {
      dotInterval = setInterval(() => {
        setLoadingDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);
    } else {
      setLoadingDots("");
    }
    return () => clearInterval(dotInterval);
  }, [isLoading]);

  // Efeito de digitação para exibir o texto gradualmente
  useEffect(() => {
    if (!apiResult || apiResult.trim() === "") {
      setTypedText("");
      setTypedHtml("");
      return;
    }

    setTypedText("");
    setTypedHtml("");
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (currentIndex >= apiResult.length) {
        clearInterval(interval);
        // Garante que o texto completo seja exibido no final
        setTypedHtml(parseBold(apiResult));
        return;
      }
      
      const newText = apiResult.substring(0, currentIndex + 1);
      currentIndex++;

      // Converte para HTML com suporte a **negrito**
      setTypedHtml(parseBold(newText));
      setTypedText(newText);
    }, 10);

    return () => {
      clearInterval(interval);
    };
  }, [apiResult]);

  // Renderização
  if (isLoading) {
    return (
      <div className="loading-container">
        <span>Processando{loadingDots}</span>
      </div>
    );
  }

  return (
    <div
      style={{ maxHeight: "300px", overflowY: "auto" }}
      ref={contentRef}  // contentRef definido via useRef
    >
      {typedHtml ? (
        <div
          className="api-response text-color-cinza5"
          style={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: typedHtml }}
        />
      ) : (
        <div className="api-response text-color-cinza5" style={{ whiteSpace: "pre-wrap" }}>
          {apiResult || "Aguardando resposta..."}
        </div>
      )}
    </div>
  );

};

export default SuperInsightsContent;
